import React, { useState } from 'react';
import { X, Coins, Timer, Info, ChevronRight, Star, Sparkles, User, Trophy, Medal, Gift, CalendarClock, Swords, Camera, Copy, Edit2, Check, Clock, DollarSign, RotateCw } from 'lucide-react';
import { QuestsPanel } from './game/modals/Quests/QuestsPanel';
import { TournamentsPanel } from './widget/tournaments/TournamentsPanel';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onActivateBonus: (spins: number, multiplier: number) => void;
  balance: number;
  setBalance: (balance: number) => void;
  activeBonus: ActiveBonus | null;
}

interface BonusOffer {
  id: string;
  name: string;
  price: number;
  features: {
    spins?: number;
    multiplier?: number;
    duration?: number;
  };
  description: string;
}

interface JackpotCampaign {
  id: string;
  name: string;
  image: string;
  entryFee: number;
  startDate: string;
  endDate: string;
  status: 'active' | 'expired';
  rewardType: 'free_spins' | 'money' | 'game_coins' | 'exp';
  rewardAmount: number;
  participated: boolean;
  won: boolean;
  totalPrizePool: number;
  participants: number;
  description: string;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, onActivateBonus, balance, setBalance, activeBonus }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [nickname, setNickname] = useState('CoolPlayer123');
  const [tempNickname, setTempNickname] = useState(nickname);
  const [selectedJackpot, setSelectedJackpot] = useState<string | null>(null);
  const playerId = 'PLR-2024-0392847';
  const level = 5;
  const xp = 2750;
  const xpRequired = 5000;
  const gameCoins = 5000;

  const jackpotCampaigns: JackpotCampaign[] = [
    {
      id: 'mega-jackpot-2024',
      name: 'Mega Jackpot 2024',
      image: 'https://images.unsplash.com/photo-1518544801976-3e159e50e5bb?w=800&h=400&fit=crop',
      entryFee: 50,
      startDate: '2024-03-01',
      endDate: '2024-03-31',
      status: 'active',
      rewardType: 'money',
      rewardAmount: 10000,
      participated: true,
      won: false,
      totalPrizePool: 50000,
      participants: 234,
      description: 'Join the biggest jackpot of the year with a massive prize pool!'
    },
    {
      id: 'spring-spins',
      name: 'Spring Spins Bonanza',
      image: 'https://images.unsplash.com/photo-1587583484084-8f9f784ce58b?w=800&h=400&fit=crop',
      entryFee: 20,
      startDate: '2024-02-15',
      endDate: '2024-02-29',
      status: 'expired',
      rewardType: 'free_spins',
      rewardAmount: 500,
      participated: true,
      won: true,
      totalPrizePool: 25000,
      participants: 156,
      description: 'Celebrate spring with amazing free spins rewards!'
    },
    {
      id: 'loyalty-boost',
      name: 'Loyalty Boost Challenge',
      image: 'https://images.unsplash.com/photo-1533228876829-65c94e7b5025?w=800&h=400&fit=crop',
      entryFee: 10,
      startDate: '2024-01-01',
      endDate: '2024-01-31',
      status: 'expired',
      rewardType: 'exp',
      rewardAmount: 5000,
      participated: false,
      won: false,
      totalPrizePool: 100000,
      participants: 543,
      description: 'Boost your loyalty level with massive XP rewards!'
    }
  ];

  if (!isOpen) return null;

  const bonusOffers: BonusOffer[] = [
    {
      id: 'lucky-spins',
      name: 'Lucky Spins Pack',
      price: 4.99,
      features: {
        spins: 10,
        multiplier: 2
      },
      description: '10 spins with 2x multiplier'
    },
    {
      id: 'mega-multiplier',
      name: 'Mega Multiplier',
      price: 9.99,
      features: {
        spins: 15,
        multiplier: 3
      },
      description: '15 spins with 3x multiplier'
    },
    {
      id: 'super-boost',
      name: 'Super Boost',
      price: 14.99,
      features: {
        spins: 20,
        multiplier: 5
      },
      description: '20 spins with 5x multiplier'
    },
    {
      id: 'ultimate-pack',
      name: 'Ultimate Pack',
      price: 24.99,
      features: {
        spins: 50,
        multiplier: 3
      },
      description: '50 spins with 3x multiplier'
    }
  ];

  const handleBuyBonus = (offer: BonusOffer) => {
    // Check if there's already an active bonus
    if (activeBonus) {
      alert('You already have an active bonus!');
      return;
    }

    if (balance >= offer.price) {
      setBalance(balance - offer.price);
      onActivateBonus(offer.features.spins!, offer.features.multiplier!);
      onClose();
    }
  };

  const handleSaveNickname = () => {
    setNickname(tempNickname);
    setIsEditingNickname(false);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const renderProfile = () => (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="flex items-start gap-4">
        <div className="relative group">
          <img
            src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=faces&auto=format&q=80"
            alt="Player Avatar"
            className="w-24 h-24 rounded-xl border-2 border-gray-200 group-hover:border-yellow-400 transition-colors"
          />
          <button className="absolute bottom-2 right-2 bg-white rounded-full p-1.5 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {isEditingNickname ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={tempNickname}
                  onChange={(e) => setTempNickname(e.target.value)}
                  className="px-2 py-1 border rounded-md text-lg font-semibold"
                  autoFocus
                />
                <button
                  onClick={handleSaveNickname}
                  className="text-green-600 hover:text-green-700"
                >
                  <Check className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-semibold">{nickname}</h3>
                <button
                  onClick={() => setIsEditingNickname(true)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
            <span>{playerId}</span>
            <button
              onClick={() => copyToClipboard(playerId)}
              className="text-gray-400 hover:text-gray-600"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>

          {/* Level Progress */}
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="font-medium">Level {level}</span>
              <span className="text-gray-500">{xp}/{xpRequired} XP</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-yellow-400"
                style={{ width: `${(xp / xpRequired) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-4 rounded-xl text-white">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium opacity-90">Balance</span>
            <Coins className="w-5 h-5 opacity-80" />
          </div>
          <div className="text-2xl font-bold">
            {balance.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 rounded-xl text-white">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium opacity-90">Game Coins</span>
            <Star className="w-5 h-5 opacity-80" />
          </div>
          <div className="text-2xl font-bold">
            {gameCoins.toLocaleString()} GC
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h4 className="text-sm font-medium text-gray-600 mb-3">Statistics</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500">Total Spins</div>
            <div className="text-lg font-semibold">1,234</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Biggest Win</div>
            <div className="text-lg font-semibold">
              {(750).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Win Rate</div>
            <div className="text-lg font-semibold">32%</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Total Winnings</div>
            <div className="text-lg font-semibold">
              {(12500).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBonuses = () => (
    <div className="grid gap-4">
      {bonusOffers.map((offer) => (
        <div
          key={offer.id}
          className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-3 sm:p-4 border border-gray-100 hover:shadow-lg transition-shadow"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2 truncate">
                {offer.name}
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 flex-shrink-0" />
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm mt-1 truncate">{offer.description}</p>
            </div>
            <div className="text-lg sm:text-xl font-bold text-green-600 flex-shrink-0">
              {offer.price.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 sm:gap-2 my-2 sm:my-3">
            {offer.features.spins && (
              <div className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 flex-shrink-0">
                <Coins className="w-3 h-3" />
                {offer.features.spins} spins
              </div>
            )}
            {offer.features.multiplier && (
              <div className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 flex-shrink-0">
                <Star className="w-3 h-3" />
                {offer.features.multiplier}x
              </div>
            )}
          </div>

          <div className="flex flex-col xs:flex-row items-start xs:items-center justify-between gap-2 mt-2 sm:mt-3">
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <Info className="w-3 h-3" />
              Limited time offer
            </div>
            <button
              onClick={() => handleBuyBonus(offer)}
              disabled={balance < offer.price || activeBonus !== null}
              className="w-full xs:w-auto bg-green-500 hover:bg-green-600 disabled:bg-gray-400 
                text-white px-3 sm:px-4 py-1.5 rounded-lg text-sm font-medium 
                flex items-center justify-center gap-1.5 transition-colors"
            >
              Buy Now
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderJackpots = () => (
    <div className="flex gap-4 h-full">
      {/* Sidebar */}
      <div className="w-64 flex-shrink-0 border-r pr-4">
        <div className="space-y-2">
          {jackpotCampaigns.map((campaign) => (
            <button
              key={campaign.id}
              onClick={() => setSelectedJackpot(campaign.id)}
              className={`w-full text-left p-3 rounded-lg transition-colors
                ${selectedJackpot === campaign.id
                  ? 'bg-yellow-50 border-yellow-200 border'
                  : 'hover:bg-gray-50 border border-transparent'
                }
              `}
            >
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-medium text-sm truncate">{campaign.name}</h3>
                <div className={`px-2 py-0.5 rounded text-xs
                  ${campaign.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {campaign.status}
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Clock className="w-3 h-3" />
                <span>Ends {new Date(campaign.endDate).toLocaleDateString()}</span>
              </div>
              {campaign.participated && (
                <div className={`mt-1 text-xs
                  ${campaign.won ? 'text-green-600' : 'text-orange-600'}`}
                >
                  {campaign.won ? '🏆 Winner!' : '🎲 Participated'}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        {selectedJackpot ? (
          (() => {
            const campaign = jackpotCampaigns.find(c => c.id === selectedJackpot);
            if (!campaign) return null;

            return (
              <div className="space-y-6">
                {/* Header Image */}
                <div className="relative rounded-xl overflow-hidden">
                  <img
                    src={campaign.image}
                    alt={campaign.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <h2 className="text-2xl font-bold mb-2">{campaign.name}</h2>
                    <p className="text-sm opacity-90">{campaign.description}</p>
                  </div>
                </div>

                {/* Campaign Details */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-500 mb-1">Entry Fee</div>
                    <div className="font-semibold flex items-center gap-1">
                      <Coins className="w-4 h-4 text-yellow-500" />
                      {campaign.entryFee} GC
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-500 mb-1">Prize Pool</div>
                    <div className="font-semibold flex items-center gap-1">
                      <DollarSign className="w-4 h-4 text-green-500" />
                      {campaign.totalPrizePool.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-500 mb-1">Participants</div>
                    <div className="font-semibold flex items-center gap-1">
                      <User className="w-4 h-4 text-blue-500" />
                      {campaign.participants}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-500 mb-1">Reward</div>
                    <div className="font-semibold flex items-center gap-1">
                      {campaign.rewardType === 'money' && <DollarSign className="w-4 h-4 text-green-500" />}
                      {campaign.rewardType === 'free_spins' && <RotateCw className="w-4 h-4 text-blue-500" />}
                      {campaign.rewardType === 'game_coins' && <Coins className="w-4 h-4 text-yellow-500" />}
                      {campaign.rewardType === 'exp' && <Star className="w-4 h-4 text-purple-500" />}
                      {campaign.rewardAmount} {campaign.rewardType === 'money' ? '€' :
                        campaign.rewardType === 'free_spins' ? 'Spins' :
                          campaign.rewardType === 'game_coins' ? 'GC' : 'XP'}
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold mb-3">Campaign Timeline</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="text-sm text-gray-500">Start Date</div>
                      <div className="font-medium">{new Date(campaign.startDate).toLocaleDateString()}</div>
                    </div>
                    <div className="h-0.5 flex-1 bg-gray-200 relative">
                      <div
                        className={`absolute inset-y-0 left-0 ${campaign.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                          }`}
                        style={{
                          width: `${Math.min(
                            100,
                            Math.max(
                              0,
                              ((new Date().getTime() - new Date(campaign.startDate).getTime()) /
                                (new Date(campaign.endDate).getTime() - new Date(campaign.startDate).getTime())) *
                              100
                            )
                          )}%`,
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-500">End Date</div>
                      <div className="font-medium">{new Date(campaign.endDate).toLocaleDateString()}</div>
                    </div>
                  </div>
                </div>

                {/* Status and Actions */}
                <div className="flex items-center justify-between border-t pt-4">
                  <div className="space-y-1">
                    <div className="text-sm text-gray-500">Status</div>
                    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm
                      ${campaign.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      <div className={`w-2 h-2 rounded-full
                        ${campaign.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}`}
                      />
                      {campaign.status === 'active' ? 'Active' : 'Expired'}
                    </div>
                  </div>
                  {campaign.status === 'active' && !campaign.participated && (
                    <button
                      className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg
                        flex items-center gap-2 font-medium transition-colors"
                    >
                      Join Now
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                  {campaign.participated && (
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg
                      ${campaign.won
                        ? 'bg-green-100 text-green-700'
                        : 'bg-orange-100 text-orange-700'
                      }`}
                    >
                      {campaign.won ? (
                        <>
                          <Trophy className="w-5 h-5" />
                          Winner!
                        </>
                      ) : (
                        <>
                          <Info className="w-5 h-5" />
                          Better luck next time
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500">
            Select a jackpot campaign to view details
          </div>
        )}
      </div>
    </div>
  );

  const menuItems = [
    { id: 'profile', icon: User, label: 'Profile' },
    { id: 'jackpots', icon: Trophy, label: 'Jackpots' },
    { id: 'bonuses', icon: Gift, label: 'Bonuses' },
    { id: 'quests', icon: Medal, label: 'Quests' },
    { id: 'tournaments', icon: Swords, label: 'Tournaments' },
    { id: 'achievements', icon: Star, label: 'Achievements' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden animate-[slideIn_0.3s_ease-out] flex flex-col">
          {/* Header with Persistent Player Info */}
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg sm:text-xl font-bold text-white">Player Dashboard</h2>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors rounded-full p-1 hover:bg-black/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Persistent Player Info */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Avatar */}
              <div className="relative group">
                <img
                  src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=faces&auto=format&q=80"
                  alt="Player Avatar"
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white/50 group-hover:border-white transition-colors"
                />
                <button className="absolute bottom-0 right-0 bg-white rounded-full p-0.5 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-600" />
                </button>
              </div>

              {/* Player Quick Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <h3 className="text-white font-semibold text-sm truncate">{nickname}</h3>
                  <div className="bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full flex-shrink-0">
                    Lvl {level}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-white/90 text-xs">
                  <span>{balance.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</span>
                  <span>{gameCoins.toLocaleString()} GC</span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="border-b relative flex-shrink-0">
            {/* Fade Overlays */}
            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

            {/* Draggable Navigation */}
            <div className="flex overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`flex items-center gap-1 px-3 sm:px-4 py-2.5 transition-colors whitespace-nowrap text-sm
                    ${activeTab === item.id
                      ? 'text-yellow-600 border-b-2 border-yellow-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto p-3 sm:p-4 flex-1">
            {activeTab === 'profile' && renderProfile()}
            {activeTab === 'bonuses' && renderBonuses()}
            {activeTab === 'jackpots' && renderJackpots()}
            {activeTab === 'quests' && <QuestsPanel />}
            {activeTab === 'tournaments' && <TournamentsPanel />}
            {activeTab === 'achievements' && <div>Achievements content</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;